import { AdminInsurerListTemplate } from '@/components/templates';
import { useAdminInsurerList } from '@/hooks/admin';
import { useRedirectWhenPageEmpty } from '@/hooks/useRedirectWhenPageEmpty';
import { useGlobalBehavior } from '@/shared/providers/GlobalBehaviorWrapper';
import { Insurer, InsurerFilterParams } from '@/types/admin-insurer';
import { japaneseDateToISO } from '@/utils/dateConverter';
import { SorterResult } from 'antd/es/table/interface';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const SiteAdminInsurerPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setEnterKeyNavigationEnabled } = useGlobalBehavior();

  // Disable Enter key navigation on this page (used for search inputs)
  useEffect(() => {
    setEnterKeyNavigationEnabled(false);
    return () => setEnterKeyNavigationEnabled(true);
  }, [setEnterKeyNavigationEnabled]);

  const page = searchParams.get('page') || '1';
  const sortBy = searchParams.get('sort_by');
  const order = searchParams.get('order') as 'ascend' | 'descend' | null;
  const searchInsurerNumber = searchParams.get('search_insurer_number');
  const searchInsurerName = searchParams.get('search_insurer_name');
  const searchInsurerNameKana = searchParams.get('search_insurer_name_kana');
  const searchInsurerTypeId = searchParams.get('search_insurer_type_id');
  const availableDate = searchParams.get('available_date');
  const prefectureId = searchParams.get('prefecture_id');

  const breadcrumbItems = [
    {
      title: '保険者',
    },
  ];

  const removeDashNumber = (value: string): string => {
    if (!value) return '';
    return value.replace(/-\d+$/, '');
  };

  const { data, isLoading, isError: listError, refetch } = useAdminInsurerList({
    page,
    sort_by: sortBy || undefined,
    order: order || undefined,
    search_insurer_number: removeDashNumber(searchInsurerNumber as string) || undefined,
    search_insurer_name: searchInsurerName || undefined,
    search_insurer_name_kana: searchInsurerNameKana || undefined,
    search_insurer_type_id: searchInsurerTypeId || undefined,
    available_date: japaneseDateToISO(availableDate as string) || undefined,
    prefecture_id: prefectureId || undefined,
  });

  const listData = data?.data || [];
  useRedirectWhenPageEmpty({
    searchParams,
    setSearchParams,
    isLoading,
    isError: listError,
    dataLength: listData.length,
  });

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const handleSearch = (filters: InsurerFilterParams) => {
    const params = new URLSearchParams(searchParams);

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.set(key, value.toString());
      } else {
        params.delete(key);
      }
    });

    params.set('page', '1');
    setSearchParams(params);
    refetch();
  };

  const handleTableChange = (sorter: SorterResult<Insurer> | SorterResult<Insurer>[]) => {
    const params = new URLSearchParams(searchParams);
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (singleSorter.order) {
      params.set('sort_by', (singleSorter.field || singleSorter.columnKey) as string);
      params.set('order', singleSorter.order);
    } else {
      params.delete('sort_by');
      params.delete('order');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  return (
    <AdminInsurerListTemplate
      data={listData}
      total={data?.meta.total || 0}
      current={data?.meta.current_page || 1}
      pageSize={data?.meta.per_page || 20}
      sortInfo={{
        columnKey: sortBy || undefined,
        order: order || undefined,
      }}
      onChange={handlePageChange}
      onTableChange={handleTableChange}
      onSearch={handleSearch}
      breadcrumbItems={breadcrumbItems}
      isLoading={isLoading}
    />
  );
};

export default SiteAdminInsurerPage;
